/*
 * Gadget Mania — Minmaxify Order Limits + Broadcast quantity sync
 *
 * Minmaxify sets min/step on input[name=quantity] (e.g. min 50 for "Con Stampa: Sì").
 * Broadcast QuantityCounter keeps an internal counter (often 1) and ++ makes 50 → 2.
 * Quantity-X may add another input[name=quantity] — Minmaxify/cart can read the wrong one.
 */
(function () {
  function parseQty(value) {
    const n = parseInt(String(value ?? '').replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) ? n : 0;
  }

  function getProductForm() {
    return document.querySelector('form[action*="/cart/add"]');
  }

  function getPrimaryQtyField() {
    const form = getProductForm();
    if (!form) return null;
    return (
      form.querySelector('[data-quantity-field][name="quantity"]') ||
      form.querySelector('input[name="quantity"]')
    );
  }

  function getAllQtyFields() {
    const fields = [];
    const form = getProductForm();

    if (form) {
      form.querySelectorAll('input[name="quantity"]').forEach((el) => fields.push(el));
    }

    document
      .querySelectorAll(
        '#quantity-discount-app input[name="quantity"], #shopify-block-quantity_x_quantity_discount_app_wiamJD input[name="quantity"], [data-product-form] input[name="quantity"]'
      )
      .forEach((el) => {
        if (!fields.includes(el)) fields.push(el);
      });

    return fields;
  }

  function getCanonicalQuantity() {
    const field = getPrimaryQtyField();
    return field ? parseQty(field.value) : 0;
  }

  function getLimits(field) {
    const min = parseQty(field.getAttribute('mm-min') || field.getAttribute('min'));
    const step = parseQty(field.getAttribute('mm-step') || field.getAttribute('step')) || 1;
    const maxAttr = field.getAttribute('mm-max') || field.getAttribute('max');
    const max = maxAttr ? parseQty(maxAttr) : null;
    return { min, step, max: max || null };
  }

  function hasMinmaxifyLimits(field) {
    if (!field) return false;
    const min = parseQty(field.getAttribute('mm-min') || field.getAttribute('min'));
    return min > 1;
  }

  function variantHasConStampa(variant) {
    if (!variant) return false;
    const parts = [variant.title, ...(variant.options || [])];
    return parts.some((part) => {
      const text = String(part).trim();
      if (!text) return false;
      if (/^no$/i.test(text)) return false;
      return /s[iìí]/i.test(text);
    });
  }

  function clearLimitAttributes(field) {
    ['mm-min', 'mm-max', 'mm-step', 'min', 'max', 'step'].forEach((attr) => {
      field.removeAttribute(attr);
    });
  }

  function syncQuantityEverywhere(qty, sourceField) {
    const value = parseQty(qty);
    const targets = getAllQtyFields();
    const primary = getPrimaryQtyField();

    if (primary && !targets.includes(primary)) targets.unshift(primary);
    if (sourceField && !targets.includes(sourceField)) targets.push(sourceField);

    targets.forEach((field) => {
      if (parseQty(field.value) === value) return;
      field.value = String(value);
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    });

    return value;
  }

  function forceResetToOne(field) {
    clearLimitAttributes(field);
    return syncQuantityEverywhere(1, field);
  }

  function setQuantity(field, qty) {
    const { min, max } = getLimits(field);
    let next = parseQty(qty);

    if (min > 0 && next < min) next = min;
    if (max && next > max) next = max;

    return syncQuantityEverywhere(next, field);
  }

  function adjustQuantity(field, direction) {
    const { min, step, max } = getLimits(field);
    let current = parseQty(field.value);

    if (min > 0 && current < min) current = min;

    let next;
    if (direction > 0) {
      next = current < min ? min : current + step;
    } else {
      next = current <= min ? min : Math.max(min, current - step);
    }

    if (max && next > max) next = max;
    return setQuantity(field, next);
  }

  function patchCartAddPayload(body) {
    const qty = getCanonicalQuantity();
    if (!qty || typeof body !== 'string') return body;

    try {
      const params = new URLSearchParams(body);
      if (params.has('quantity')) {
        params.set('quantity', String(qty));
        return params.toString();
      }
    } catch (_) {
      /* keep original body */
    }

    return body;
  }

  function installCartAddPatch() {
    if (window.__gmCartAddPatch) return;
    window.__gmCartAddPatch = true;

    if (window.fetch && !window.fetch.__gmQtyPatch) {
      const originalFetch = window.fetch;
      const patchedFetch = function (input, init) {
        let nextInit = init;

        try {
          const url = (typeof input === 'string' ? input : input?.url || '').toString();
          if (nextInit?.body && url.includes('/cart/add')) {
            const body = patchCartAddPayload(
              typeof nextInit.body === 'string' ? nextInit.body : null
            );
            if (body) nextInit = { ...nextInit, body };
          }
        } catch (_) {
          /* noop */
        }

        return originalFetch.call(this, input, nextInit);
      };
      patchedFetch.__gmQtyPatch = true;
      window.fetch = patchedFetch;
    }

    const xhrProto = XMLHttpRequest.prototype;
    if (!xhrProto.__gmQtySendPatch) {
      const originalSend = xhrProto.send;
      xhrProto.send = function (body) {
        try {
          const url = String(this.mmUrl || '');
          if (body && url.includes('/cart/add')) {
            body = patchCartAddPayload(body);
          }
        } catch (_) {
          /* noop */
        }
        return originalSend.call(this, body);
      };
      xhrProto.__gmQtySendPatch = true;
    }
  }

  function bindPrimaryQtyInput() {
    const field = getPrimaryQtyField();
    if (!field || field.dataset.gmQtyInputBound === '1') return;
    field.dataset.gmQtyInputBound = '1';

    const onQtyInput = () => syncQuantityEverywhere(parseQty(field.value), field);

    field.addEventListener('input', onQtyInput);
    field.addEventListener('change', onQtyInput);
  }

  function bindAddToCartSync() {
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-add-to-cart]');
        if (!button) return;

        const form = button.closest('form[action*="/cart/add"]');
        if (!form) return;

        syncQuantityEverywhere(getCanonicalQuantity());
      },
      true
    );
  }

  function patchQuantityButtons() {
    const form = getProductForm();
    if (!form) return;

    form.querySelectorAll('[data-quantity-holder]').forEach((holder) => {
      if (holder.dataset.gmQtyFix === '1') return;

      const field = holder.querySelector('[data-quantity-field], input[name="quantity"]');
      const plus = holder.querySelector('[data-quantity-plus]');
      const minus = holder.querySelector('[data-quantity-minus]');
      if (!field || !plus) return;

      holder.dataset.gmQtyFix = '1';

      plus.addEventListener(
        'click',
        (event) => {
          if (!hasMinmaxifyLimits(field)) return;

          event.stopImmediatePropagation();
          event.preventDefault();
          adjustQuantity(field, 1);
        },
        true
      );

      minus.addEventListener(
        'click',
        (event) => {
          if (!hasMinmaxifyLimits(field)) return;

          event.stopImmediatePropagation();
          event.preventDefault();
          adjustQuantity(field, -1);
        },
        true
      );
    });
  }

  function syncAfterMinmaxify() {
    const field = getPrimaryQtyField();
    if (!field || !hasMinmaxifyLimits(field)) return;

    const { min } = getLimits(field);
    const current = parseQty(field.value);
    if (current < min) setQuantity(field, min);
  }

  function reconcileQuantity(event) {
    const field = getPrimaryQtyField();
    if (!field) return;

    const variant = event?.detail?.variant;

    if (variant && !variantHasConStampa(variant)) {
      forceResetToOne(field);
      return;
    }

    if (variant && variantHasConStampa(variant)) {
      if (hasMinmaxifyLimits(field)) {
        syncAfterMinmaxify();
      } else {
        window.setTimeout(syncAfterMinmaxify, 200);
      }
      return;
    }

    if (hasMinmaxifyLimits(field)) {
      syncAfterMinmaxify();
    } else if (parseQty(field.value) !== 1) {
      forceResetToOne(field);
    }
  }

  function onVariantOrStampaChange(event) {
    reconcileQuantity(event);
    window.setTimeout(() => reconcileQuantity(event), 350);
    window.setTimeout(() => reconcileQuantity(event), 900);
    patchQuantityButtons();
    bindPrimaryQtyInput();
  }

  function observeMinmaxifyAttributes() {
    const field = getPrimaryQtyField();
    if (!field) return;

    const observer = new MutationObserver(() => {
      if (hasMinmaxifyLimits(field)) {
        syncAfterMinmaxify();
        return;
      }

      if (parseQty(field.value) !== 1) {
        forceResetToOne(field);
      }
    });

    observer.observe(field, {
      attributes: true,
      attributeFilter: ['value', 'min', 'max', 'step', 'mm-min', 'mm-max', 'mm-step'],
    });
  }

  function init() {
    if (!getProductForm()) return;

    installCartAddPatch();
    bindAddToCartSync();
    bindPrimaryQtyInput();
    patchQuantityButtons();
    observeMinmaxifyAttributes();
    reconcileQuantity({});

    document.addEventListener('theme:variant:change', onVariantOrStampaChange);

    const form = getProductForm();
    form.addEventListener('change', (event) => {
      if (event.target.matches('[name^="options"], [name="id"]')) {
        window.setTimeout(() => reconcileQuantity({}), 80);
      }
    });

    window.setTimeout(() => {
      installCartAddPatch();
      reconcileQuantity({});
    }, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
